// ARCA-677 — resolve the dynamic `--match-chrome` reserve (px).
//
// The board-stack caps its height to
//   min(100%, (100svh − var(--match-chrome) − safe-area) * cols / rows)
// (MatchScreen.tsx board-stack `width` calc) so the WORD/SKIP/MOVE/EMPOWER
// action dock stays on-screen as the field grows 6→8→10 rows. For the cap to be
// correct the reserve MUST equal the real NON-board vertical chrome — the top
// bar above the board plus everything below it (the affinity rune row + the
// control deck). The root <main> is a gap-less flex column, so:
//
//   chrome = topBar height + affinity-row height + control-deck height
//
// The old value was a hardcoded `--match-chrome: 260px` (pieces.css), tuned for
// one device. Whenever the real chrome exceeded 260px — the coach HintButton in
// the first 5 matches, the taller chess-phase deck, the staged-word readout, a
// larger safe-area, longer localized labels — the board was over-allocated and
// its bottom row(s) clipped off-screen at 6×8 / 6×10 (ARCA-594). Measuring the
// chrome instead of guessing it keeps the whole board on-screen across phases
// and devices.
//
// This pure helper isolates the (trivial but contract-bearing) reduction so it
// is unit-testable without a DOM; the measurement itself lives in MatchScreen
// (a `useLayoutEffect` + `ResizeObserver`, mirroring the `--board-top` pattern).
// None of the three parts depends on the board height (they are flow siblings,
// not the flexed board), so feeding the result back into `--match-chrome` never
// forms a measure→resize→measure loop.

export interface MatchChromeParts {
  /** Measured top-bar (`.match-topbar`) border-box height, px. */
  readonly topBarPx: number
  /**
   * Measured affinity rune row (`[data-screen-region="affinity-chip-row"]`)
   * border-box height, px. 0 when the row is not mounted (chess view).
   */
  readonly affinityPx: number
  /** Measured control-deck (`[data-screen-region="control-deck"]`) height, px. */
  readonly deckPx: number
}

/**
 * Sum the measured chrome parts into the `--match-chrome` reserve.
 *
 * Negative inputs are clamped to 0 (a defensive guard — `offsetHeight` is never
 * negative in practice). The total is rounded UP so sub-pixel measurements
 * always over-reserve by <1px rather than under-reserving and clipping the
 * bottom row.
 *
 * Returns `null` when nothing has been measured yet (every part 0 — the
 * elements are absent from the DOM, or a measure ran before first paint). A
 * null result means "leave `--match-chrome` unset" so the CSS fallback (260px
 * in pieces.css) holds until a real measurement lands; that avoids a 0px
 * reserve that would let the board balloon over the dock for a frame.
 */
export function resolveMatchChromePx({
  topBarPx,
  affinityPx,
  deckPx,
}: MatchChromeParts): number | null {
  const total =
    Math.max(0, topBarPx) + Math.max(0, affinityPx) + Math.max(0, deckPx)
  return total > 0 ? Math.ceil(total) : null
}
